import {
  AssignMappingItem,
  GENERIC_CID,
  GlobalMappingsResponse,
  MappingActionResult,
  MappingItemType,
  MappingRow,
  MappingSearchResult,
  ReassignMappingItem,
  UnassignedMappingRow,
  UsersByDomainResponse
} from '@blisspointmedia/bpm-types/dist/ClientDataMapping';
import { awaitJSON, OracleLambdaFetch, UsersLambdaFetch } from '../../utils/fetch-utils';

export const fetchClientMappings = async (
  company: string,
  itemType: MappingItemType
): Promise<MappingRow[]> => {
  const res = await OracleLambdaFetch('/mappings', { params: { company, itemType } });
  const { rows } = await awaitJSON<{ rows: MappingRow[] }>(res);
  return rows;
};

// The endpoint returns a single heterogeneous `rows` list. MappingRow (has `cid`) means an
// (item, cid) pair -- active if `unassignedAt` is null, soft-deleted if it's set (only
// possible when the caller sends `modifiedSince`, which the frontend never does). No `cid`
// means an item with no active assignment. Narrowing via `'cid' in row` distinguishes the two.
const isMappingRow = (row: MappingRow | UnassignedMappingRow): row is MappingRow => 'cid' in row;

interface GlobalMappingsForType {
  assigned: MappingRow[];
  unassigned: UnassignedMappingRow[];
  // Populated only for item types that permit generic (public/shared) assignments
  // (slack_channel and email_domain). MappingRow whose cid equals GENERIC_CID goes here
  // instead of `assigned` so the Global-Admin SegmentedControl can surface it under a
  // distinct "Generic" segment. For non-allowGeneric tabs this is always empty.
  generic: MappingRow[];
}

// Global-admin per-type view. The server returns everything in one flat list; the assigned /
// unassigned / generic split is derived here so the sub-tab toggle can switch views without a
// round-trip. itemType filters the server-side query, so the payload is scoped to the tab.
//
// `rows` is defaulted to [] on the destructure -- the server should always send it, but a
// stale lambda deploy or a partial response should still render as an empty tab rather than
// crashing the page on `.filter` of undefined.
export const fetchGlobalMappings = async (
  itemType: MappingItemType
): Promise<GlobalMappingsForType> => {
  const res = await OracleLambdaFetch('/mappings/global', {
    params: { itemType, includeNeverAssigned: true },
    // React StrictMode double-invokes effects in dev, producing a second batch of fetches
    // while the first is still in-flight. This is expected and harmless (both responses
    // write the same data), so suppress the duplicate-detection warning.
    devAllowDuplicate: true
  });
  const { rows = [] } = await awaitJSON<Partial<GlobalMappingsResponse>>(res);
  const assigned: MappingRow[] = [];
  const unassigned: UnassignedMappingRow[] = [];
  const generic: MappingRow[] = [];
  rows.forEach(row => {
    if (!isMappingRow(row)) {
      unassigned.push(row);
      return;
    }
    if (row.unassignedAt) {
      // Soft-deleted MappingRow (only reachable via modifiedSince) -- surface as an
      // UnassignedMappingRow so the Un-assigned tab renders it uniformly with orphan items.
      const { cid, isManual, lastModifiedBy, lastModified, unassignedAt, ...rest } = row;
      unassigned.push(rest);
      return;
    }
    if (row.cid === GENERIC_CID) {
      generic.push(row);
      return;
    }
    assigned.push(row);
  });
  return { assigned, unassigned, generic };
};

export const searchMappingItems = async (
  company: string,
  itemType: MappingItemType,
  q: string
): Promise<MappingSearchResult[]> => {
  const res = await OracleLambdaFetch('/mappings/search', { params: { company, itemType, q } });
  const { results } = await awaitJSON<{ results: MappingSearchResult[] }>(res);
  return results;
};

export const assignMappingItems = async (
  company: string,
  itemType: MappingItemType,
  items: AssignMappingItem[]
): Promise<MappingActionResult[]> => {
  const res = await OracleLambdaFetch('/mappings/assign', {
    method: 'POST',
    body: { company, itemType, items }
  });
  const { results } = await awaitJSON<{ results: MappingActionResult[] }>(res);
  return results;
};

export const unassignMappingItems = async (
  company: string,
  itemType: MappingItemType,
  itemIds: number[]
): Promise<MappingActionResult[]> => {
  const res = await OracleLambdaFetch('/mappings/unassign', {
    method: 'POST',
    body: { company, itemType, itemIds }
  });
  const { results } = await awaitJSON<{ results: MappingActionResult[] }>(res);
  return results;
};

export const reassignMappingItems = async (
  itemType: MappingItemType,
  items: ReassignMappingItem[]
): Promise<MappingActionResult[]> => {
  const res = await OracleLambdaFetch('/mappings/reassign', {
    method: 'POST',
    body: { itemType, items }
  });
  const { results } = await awaitJSON<{ results: MappingActionResult[] }>(res);
  return results;
};

// Backs DomainUsersTooltip -- returns BPM users whose email ends with `domain` (e.g.
// '@instacart.com'). Server caps at USERS_BY_DOMAIN_LIMIT + 1 rows; the tooltip uses the +1
// to decide whether to render "+N more".
export const fetchUsersByDomain = async (domain: string): Promise<UsersByDomainResponse> => {
  const res = await OracleLambdaFetch('/mappings/users-by-domain', { params: { domain } });
  return awaitJSON<UsersByDomainResponse>(res);
};

// ─── AI blacklist ───────────────────────────────────────────────────────────
// Companies excluded from the AI/agent tool surface (company_ai_blacklist). The
// list endpoint is in the users service (GET /aiBlacklist); admin-only add/remove
// also live there.

export const fetchAiBlacklist = async (): Promise<string[]> => {
  const res = await UsersLambdaFetch('/aiBlacklist');
  const { cids } = await awaitJSON<{ cids: string[] }>(res);
  return cids;
};

export const addAiBlacklistCid = async (cid: string): Promise<string[]> => {
  const res = await UsersLambdaFetch('/aiBlacklist', { method: 'POST', body: { cid } });
  const { cids } = await awaitJSON<{ cids: string[] }>(res);
  return cids;
};

export const removeAiBlacklistCid = async (cid: string): Promise<string[]> => {
  const res = await UsersLambdaFetch('/aiBlacklist', { method: 'DELETE', params: { cid } });
  const { cids } = await awaitJSON<{ cids: string[] }>(res);
  return cids;
};
